import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, Triangle, CheckCircle2, XCircle, Clock, Printer, ThumbsUp, ThumbsDown, Lightbulb } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  PieChart, Pie, Cell, Sector,
  RadialBarChart, RadialBar
} from 'recharts';

function ReportModal({ isOpen, onClose, examData }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // 학생 정보 상태 추가
  const [studentInfo, setStudentInfo] = useState(null);

  // --- 상태 변수들을 먼저 정의 ---
  const [examInfo, setExamInfo] = useState({
    exam_type: '',
    updated_at: '',
    exam_count: 0,
    difficulty: 0,
    length: 0,
    exam_id: ''
  });

  const [fieldInfo, setFieldInfo] = useState({
    tree_name: ''
  });

  const [ratingData, setRatingData] = useState([]);

  // --- RE-ADD: API State & Fetching Logic ---
  const { getAccessToken } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  // --- Add new loading states ---
  const [retryLoading, setRetryLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);

  // 메모 입력을 위한 상태 변수 추가
  const [memoVisible, setMemoVisible] = useState(false);
  const [retryMemo, setRetryMemo] = useState('');

  // 더미 데이터 제거
  const [strengths, setStrengths] = useState([]);
  const [weaknesses, setWeaknesses] = useState([]);
  const [studyAreas, setStudyAreas] = useState([]);
  const [shortTermGoals, setShortTermGoals] = useState({ period: '', goals: '' });
  const [midTermGoals, setMidTermGoals] = useState({ period: '', goals: '' });
  const [longTermGoals, setLongTermGoals] = useState({ period: '', goals: '' });
  const [overallComment, setOverallComment] = useState('');

  // --- Edit Mode States ---
  const [isEditingStrengths, setIsEditingStrengths] = useState(false);
  const [tempStrengths, setTempStrengths] = useState(null);

  const [isEditingWeaknesses, setIsEditingWeaknesses] = useState(false);
  const [tempWeaknesses, setTempWeaknesses] = useState(null);

  const [isEditingStudyAreas, setIsEditingStudyAreas] = useState(false);
  const [tempStudyAreas, setTempStudyAreas] = useState(null);

  const [isEditingShortTermGoals, setIsEditingShortTermGoals] = useState(false);
  const [tempShortTermGoals, setTempShortTermGoals] = useState(null);

  const [isEditingMidTermGoals, setIsEditingMidTermGoals] = useState(false);
  const [tempMidTermGoals, setTempMidTermGoals] = useState(null);

  const [isEditingLongTermGoals, setIsEditingLongTermGoals] = useState(false);
  const [tempLongTermGoals, setTempLongTermGoals] = useState(null);

  const [isEditingOverallComment, setIsEditingOverallComment] = useState(false);
  const [tempOverallComment, setTempOverallComment] = useState('');

  // 학습 조언 전체 수정을 위한 상태 추가
  const [isEditingLearningGoals, setIsEditingLearningGoals] = useState(false);
  const [tempLearningGoals, setTempLearningGoals] = useState(null);

  // 1. useRef 추가
  const reportContentRef = useRef(null);

  // --- 파이 차트 상태 추가 --- 
  const [activeIndexLevel, setActiveIndexLevel] = useState(null); // 초기값 null
  const [activeIndexDomain, setActiveIndexDomain] = useState(null); // 초기값 null

  // --- 파이 차트 이벤트 핸들러 추가 ---
  const onLevelPieEnter = useCallback((_, index) => {
    setActiveIndexLevel(index);
  }, [setActiveIndexLevel]);

  const onDomainPieEnter = useCallback((_, index) => {
    setActiveIndexDomain(index);
  }, [setActiveIndexDomain]);
  
  // --- 파이 차트 관련 추가 끝 ---

  const LEVEL_PASTEL_COLORS = ['rgba(253,224,71,0.7)', 'rgba(134,239,172,0.7)', 'rgba(147,197,253,0.7)', 'rgba(253,186,116,0.7)', 'rgba(252,165,165,0.7)'];
  const LEVEL_SOLID_COLORS = ['#EAB308', '#059669', '#2563EB', '#EA580C', '#DC2626'];
  const DOMAIN_GREEN_COLORS = ['#4ABC85', '#A2E4C1'];

  const levelLegend = [
    { name: 'Lv 1', color: LEVEL_SOLID_COLORS[0] }, { name: 'Lv 2', color: LEVEL_SOLID_COLORS[1] },
    { name: 'Lv 3', color: LEVEL_SOLID_COLORS[2] }, { name: 'Lv 4', color: LEVEL_SOLID_COLORS[3] },
    { name: 'Lv 5', color: LEVEL_SOLID_COLORS[4] }
  ];
  const domainLegend = [
    { name: '다항식', color: DOMAIN_GREEN_COLORS[0] },
    { name: '방정식과 부등식', color: DOMAIN_GREEN_COLORS[1] }
  ];

  // renderActiveShape 함수 다시 추가
  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
    const cornerRadius = 8;

    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const sx = cx + (outerRadius + 10) * cos;
    const sy = cy + (outerRadius + 10) * sin;
    const mx = cx + (outerRadius + 30) * cos;
    const my = cy + (outerRadius + 30) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 22;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        {/* 중앙 텍스트 (이름, 개수, 비율 표시) */}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fill="#333">
          <tspan x={cx} dy="-0.6em" fontSize={12} fontWeight="bold">{payload.name}</tspan>
          <tspan x={cx} dy="1.2em" fontSize={10}>{`${value}개`}</tspan>
          <tspan x={cx} dy="1.2em" fontSize={10}>{`(${(percent * 100).toFixed(0)}%)`}</tspan>
        </text>
        {/* 기본 섹터 */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          cornerRadius={cornerRadius}
        />
        {/* 하이라이트 섹터 */}
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle+5}
          endAngle={endAngle-5}
          innerRadius={outerRadius + 4}
          outerRadius={outerRadius + 8}
          fill={fill}
          cornerRadius={cornerRadius}
        />
      </g>
    );
  };

  // API 데이터 처리 함수 수정
  // 차트 데이터 계산 함수
  const getRatingData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.exams || reportData.data.exams.length === 0) {
      return []; // 빈 배열 반환
    }

    // exams 배열은 최신 순으로 정렬되어 있음 (0번이 최신)
    const chartData = reportData.data.exams.map((exam, index) => ({
      name: index === 0 ? '현재 시험' : '이전 시험',
      rating: exam.rating || 0,
      examId: exam.exam_id || '',
      examRound: exam.exam_round || 0,
      date: new Date(exam.updated_at || '').toLocaleDateString()
    }));

    const reversed = chartData.reverse();
    // 이전 회차 대비 변화량 계산
    return reversed.map((item, i) => ({
      ...item,
      change: i > 0 ? item.rating - reversed[i - 1].rating : null
    }));
  }, [reportData]);

  // 폼레이팅 레벨별 색상 (진한 원색) — 도트, 배지, 수치용
  const getRatingColor = useCallback((rating) => {
    if (!rating || rating <= 0) return '#9CA3AF';
    if (rating <= 2000) return '#EAB308';
    if (rating <= 4000) return '#059669';
    if (rating <= 6000) return '#2563EB';
    if (rating <= 8000) return '#EA580C';
    return '#DC2626';
  }, []);

  // 난이도 레벨별 색상 (폼레이팅과 동일 5단계)
  const getDifficultyColor = useCallback((difficulty) => {
    if (!difficulty || difficulty <= 0) return '#9CA3AF';
    if (difficulty <= 1) return '#EAB308';
    if (difficulty <= 2) return '#059669';
    if (difficulty <= 3) return '#2563EB';
    if (difficulty <= 4) return '#EA580C';
    return '#DC2626';
  }, []);

  // 폼레이팅 레벨별 진한 색상 — Y축 눈금용
  const getRatingColorOpaque = useCallback((rating) => {
    if (!rating || rating <= 0) return '#9CA3AF';
    if (rating <= 2000) return '#EAB308';
    if (rating <= 4000) return '#059669';
    if (rating <= 6000) return '#2563EB';
    if (rating <= 8000) return '#EA580C';
    return '#DC2626';
  }, []);

  // Y축 눈금 및 범위 데이터 생성 (백 단위로 반올림)
  const getYAxisDomain = useCallback(() => {
    const ratingValues = getRatingData().map(item => item.rating);
    if (ratingValues.length === 0) return [0, 100];

    const minRating = Math.min(...ratingValues);
    const maxRating = Math.max(...ratingValues);

    return [Math.floor((minRating - 100) / 100) * 100, Math.ceil((maxRating + 100) / 100) * 100];
  }, [getRatingData]);

  // 폼레이팅 레벨별 그라데이션 stops 생성
  const getRatingGradientStops = useCallback(() => {
    const [yMin, yMax] = getYAxisDomain();
    const range = yMax - yMin;
    if (range <= 0) {
      return [{ offset: 0, color: '#EAB308', opacity: 0.3 }, { offset: 1, color: '#EAB308', opacity: 0.02 }];
    }

    const colorAt = (r) => {
      if (r <= 2000) return 'rgb(253,224,71)';
      if (r <= 4000) return 'rgb(134,239,172)';
      if (r <= 6000) return 'rgb(147,197,253)';
      if (r <= 8000) return 'rgb(253,186,116)';
      return 'rgb(252,165,165)';
    };

    const stops = [];
    stops.push({ offset: 0, color: colorAt(yMax), opacity: 0.3 });

    const boundaries = [2000, 4000, 6000, 8000];
    for (const b of boundaries) {
      if (b > yMin && b < yMax) {
        const pct = (yMax - b) / range;
        const opacity = 0.3 - pct * 0.28;
        stops.push({ offset: pct, color: colorAt(b + 1), opacity });
        stops.push({ offset: pct + 0.001, color: colorAt(b), opacity });
      }
    }

    stops.push({ offset: 1, color: colorAt(Math.max(yMin, 1)), opacity: 0.02 });
    stops.sort((a, b) => a.offset - b.offset);
    return stops;
  }, [getYAxisDomain]);

  // 현재 레이팅 값 얻기
  const getCurrentRating = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.exams || reportData.data.exams.length === 0) {
      return 0;
    }
    return reportData.data.exams[0].rating || 0;
  }, [reportData]);

  // 레이팅 변화 계산
  const getRatingChange = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.exams || reportData.data.exams.length < 2) {
      return 0;
    }
    const currentRating = reportData.data.exams[0].rating || 0;
    const previousRating = reportData.data.exams[1].rating || 0;
    return currentRating - previousRating;
  }, [reportData]);

  // 정답률 계산 함수
  const getAccuracyData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.recent_record || !reportData.data.recent_record.by_step) {
      return {
        accuracy: 0,
        correctCount: 0,
        totalCount: 0
      };
    }

    // 난이도별 맞은 문제 수와 틀린 문제 수
    const correctByLevel = reportData.data.recent_record.by_step[0] || [];
    const incorrectByLevel = reportData.data.recent_record.by_step[1] || [];
    
    // 총 맞은 문제 수
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    // 총 틀린 문제 수
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    // 총 문제 수
    const totalQuestions = totalCorrect + totalIncorrect;
    // 정답률 계산 (소수점 반올림)
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return {
      accuracy,
      correctCount: totalCorrect,
      totalCount: totalQuestions
    };
  }, [reportData]);

  // 누적 정답률 계산 함수
  const getCumulativeAccuracyData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.past_record || !reportData.data.past_record.by_step) {
      return {
        accuracy: 0,
        correctCount: 0,
        totalCount: 0
      };
    }

    // 난이도별 누적 맞은 문제 수와 틀린 문제 수
    const correctByLevel = reportData.data.past_record.by_step[0] || [];
    const incorrectByLevel = reportData.data.past_record.by_step[1] || [];
    
    // 총 누적 맞은 문제 수
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    // 총 누적 틀린 문제 수
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    // 총 누적 문제 수
    const totalQuestions = totalCorrect + totalIncorrect;
    // 누적 정답률 계산 (소수점 반올림)
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return {
      accuracy,
      correctCount: totalCorrect,
      totalCount: totalQuestions
    };
  }, [reportData]);

  // 난이도별 정답률 계산 함수
  const getLevelAchievementData = useCallback(() => {
    // API 데이터가 없는 경우 기본값 사용
    if (!reportData || !reportData.data || !reportData.data.recent_record || !reportData.data.recent_record.by_step || 
        !reportData.data.past_record || !reportData.data.past_record.by_step) {
      return [1, 2, 3, 4, 5].map(level => ({
        level,
        currentAcc: 0,
        currentCorrect: 0,
        currentTotal: 0,
        cumulativeAcc: 0,
        cumulativeCorrect: 0,
        cumulativeTotal: 0
      }));
    }

    // 현재 시험 데이터
    const recentCorrect = reportData.data.recent_record.by_step[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.data.recent_record.by_step[1] || [0, 0, 0, 0, 0];

    // 과거 누적 시험 데이터
    const pastCorrect = reportData.data.past_record.by_step[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.data.past_record.by_step[1] || [0, 0, 0, 0, 0];

    // 난이도별 정답률 계산
    return [1, 2, 3, 4, 5].map((level, index) => {
      // 현재 시험 정답률 계산
      const currentCorrect = recentCorrect[index] || 0;
      const currentIncorrect = recentIncorrect[index] || 0;
      const currentTotal = currentCorrect + currentIncorrect;
      const currentAcc = currentTotal > 0 ? Math.round((currentCorrect / currentTotal) * 100) : 0;

      // 누적 시험 정답률 계산
      const cumulativeCorrect = pastCorrect[index] || 0;
      const cumulativeIncorrect = pastIncorrect[index] || 0;
      const cumulativeTotal = cumulativeCorrect + cumulativeIncorrect;
      const cumulativeAcc = cumulativeTotal > 0 ? Math.round((cumulativeCorrect / cumulativeTotal) * 100) : 0;

      return {
        level,
        currentAcc,
        currentCorrect,
        currentTotal,
        cumulativeAcc,
        cumulativeCorrect,
        cumulativeTotal
      };
    });
  }, [reportData]);

  // 평균 난이도 계산 함수
  const getAverageDifficulty = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.exams || reportData.data.exams.length === 0) {
      return 0;
    }
    return (reportData.data.exams[0].difficulty || 0) / 100;
  }, [reportData]);

  // 누적 난이도 계산 함수
  const getCumulativeDifficulty = useCallback(() => {
    if (!reportData || !reportData.data || reportData.data.past_avg_difficulty === undefined) {
      return 0;
    }
    return (reportData.data.past_avg_difficulty || 0) / 100;
  }, [reportData]);

  // 난이도 레벨을 텍스트로 변환하는 함수
  const getDifficultyLevelText = useCallback((difficulty) => {
    if (difficulty < 1.5) return "하";
    if (difficulty < 2.5) return "중하";
    if (difficulty < 3.5) return "중";
    if (difficulty < 4.5) return "중상";
    return "상";
  }, []);

  // 난이도별 도넛 차트 데이터 생성 함수
  const getLevelDonutData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.recent_record || !reportData.data.past_record) {
      return []; // 빈 배열 반환
    }

    // 현재 시험 데이터와 누적 시험 데이터 합치기
    const recentCorrect = reportData.data.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.data.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.data.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.data.past_record.by_step?.[1] || [0, 0, 0, 0, 0];

    // 각 난이도별 맞은 문제 수와 틀린 문제 수를 합산
    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);
    
    // 각 난이도별 총 문제 수 계산
    const totalQuestions = totalCorrect.map((count, i) => count + totalIncorrect[i]);

    // 도넛 차트 데이터 형식으로 변환 (문제 수가 있는 난이도만 포함)
    return [1, 2, 3, 4, 5].map((level, index) => ({
      name: `LV ${level}`,
      value: totalQuestions[index] || 0
    })).filter(item => item.value > 0);
  }, [reportData]);

  // 유형별 도넛 차트 데이터 생성 함수 (영역별)
  const getDomainDonutData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.ref) {
      return []; // 빈 배열 반환
    }

    // recent_record와 past_record에서 by_address 데이터 가져오기
    const recentData = reportData.data.recent_record?.by_address || {};
    const pastData = reportData.data.past_record?.by_address || {};
    const refData = reportData.data.ref || {};

    // 모든 주소 키를 합치고 중복 제거
    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    // 각 주소별 총 문제 수 계산
    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      // 현재 시험 및 과거 시험 데이터
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];

      // 총 문제 수 계산 (맞은 문제 + 틀린 문제)
      const totalCount = (recent[0] + recent[1]) + (past[0] + past[1]);
      
      // 주소에 해당하는 한글 이름 찾기
      const subjectName = refData[address] || address;
      
      if (!subjectMap.has(subjectName)) {
        subjectMap.set(subjectName, 0);
      }
      
      subjectMap.set(subjectName, subjectMap.get(subjectName) + totalCount);
    });

    // 도넛 차트 데이터 형식으로 변환
    return Array.from(subjectMap.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0);
  }, [reportData]);

  // 유형별 레이더 차트 데이터 생성 함수
  const getSubjectRadarData = useCallback(() => {
    if (!reportData || !reportData.data) {
      return []; // 빈 배열 반환
    }

    // recent_record와 past_record에서 by_address 데이터 가져오기
    const recentData = reportData.data.recent_record?.by_address || {};
    const pastData = reportData.data.past_record?.by_address || {};
    const refData = reportData.data.ref || {};

    // 모든 주소 키를 합치고 중복 제거
    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    // 각 주소별 정답률 계산
    const subjectMap = new Map();

    allAddressKeys.forEach(address => {
      // 현재 시험 및 과거 시험 데이터
      const recent = recentData[address] || [0, 0];
      const past = pastData[address] || [0, 0];

      // 총 맞은 문제 수와 틀린 문제 수
      const totalCorrect = recent[0] + past[0];
      const totalIncorrect = recent[1] + past[1];
      const totalCount = totalCorrect + totalIncorrect;
      
      // 정답률 계산
      const accuracy = totalCount > 0 ? Math.round((totalCorrect / totalCount) * 100) : 0;
      
      // 주소에 해당하는 한글 이름 찾기
      const subjectName = refData[address] || address;
      
      subjectMap.set(subjectName, accuracy);
    });

    // 레이더 차트 데이터 형식으로 변환 (최대 6개만 선택)
    return Array.from(subjectMap.entries())
      .map(([subject, A]) => ({
        subject,
        A,
        fullMark: 100
      }))
      .sort((a, b) => b.A - a.A) // 정답률 높은 순으로 정렬
      // .slice(0, 6); // 최대 6개만 선택
  }, [reportData]);

  // 유형별 상세 분석 데이터 생성 함수를 수정하여 전체 결과를 포함하도록 합니다
  const getDetailedAnalysisData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.recent_record || !reportData.data.past_record || !reportData.data.ref) {
      return []; // 빈 배열 반환
    }

    // 현재 시험 데이터
    const recentData = reportData.data.recent_record.by_address || {};
    // 과거 시험 데이터
    const pastData = reportData.data.past_record.by_address || {};
    // 참조 데이터 (한글 이름)
    const refData = reportData.data.ref || {};

    // 모든 주소 키를 합치고 중복 제거
    const allAddressKeys = [...new Set([
      ...Object.keys(recentData),
      ...Object.keys(pastData)
    ])];

    // 각 주소에 대한 상세 데이터 생성
    return allAddressKeys.map(address => {
      // 현재 시험 정보 (없으면 [0, 0])
      const recent = recentData[address] || [0, 0];
      // 과거 시험 정보 (없으면 [0, 0])
      const past = pastData[address] || [0, 0];

      // 현재 시험 정답/오답 계산
      const recentCorrect = recent[0] || 0;
      const recentIncorrect = recent[1] || 0;
      const recentTotal = recentCorrect + recentIncorrect;
      
      // 과거 시험 정답/오답 계산
      const pastCorrect = past[0] || 0;
      const pastIncorrect = past[1] || 0;
      const pastTotal = pastCorrect + pastIncorrect;

      // 정답률 계산
      const recentAccuracy = recentTotal > 0 
        ? `${Math.round((recentCorrect / recentTotal) * 100)}% (${recentCorrect}/${recentTotal})`
        : '0% (0/0)';
      
      // 누적 정답률 계산
      const cumulativeAccuracy = pastTotal > 0 
        ? `${Math.round((pastCorrect / pastTotal) * 100)}% (${pastCorrect}/${pastTotal})`
        : '0% (0/0)';

      // 한글 이름 찾기
      const subCategory = refData[address] || address;
      const category = address.split('/')
      .slice(0, 2)
      .join('/');
      const categoryName = refData[category] || category;

      return {
        category: categoryName,
        subCategory,
        accuracy: recentAccuracy,
        cumulativeAccuracy
      };
    }).sort((a, b) => a.category.localeCompare(b.category));
  }, [reportData]);

  // 기존 하드코딩 데이터를 API 응답 데이터로 대체하는 함수 추가
  const getStrengthsAndWeaknesses = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.opinions) {
      return {
        strengths: [],
        weaknesses: []
      };
    }

    const apiStrengths = [];
    const apiWeaknesses = [];

    // pros(강점)와 cons(약점)를 하나의 배열로 합치기
    if (reportData.data.opinions.pros) {
      const prosItems = reportData.data.opinions.pros.split('\n\n');
      prosItems.forEach((item) => {
        if (item.trim()) {
          apiStrengths.push({ description: item.trim() });
        }
      });
    }

    if (reportData.data.opinions.cons) {
      const consItems = reportData.data.opinions.cons.split('\n\n');
      consItems.forEach((item) => {
        if (item.trim()) {
          apiWeaknesses.push({ description: item.trim() });
        }
      });
    }

    return {
      strengths: apiStrengths,
      weaknesses: apiWeaknesses
    };
  }, [reportData]);

  // 텍스트를 구조화된 블록으로 분리 (핵심 요약 + 상세 설명 + 추천 학습법)
  const renderStructuredText = useCallback((text, type) => {
    const sentences = text.split(/(?<=다\.|요\.|시오\.|니다\.|세요\.|습니다\.)\s*/);
    const recKeywords = ['권장', '추천', '필요', '바랍니다', '것을 ', '하세요', '드립니다'];

    let summary = sentences[0] || '';
    let detailSentences = [];
    let recommendation = '';

    // 마지막 문장이 추천/권장 키워드를 포함하면 분리
    if (sentences.length > 1) {
      const lastSentence = sentences[sentences.length - 1];
      if (recKeywords.some(kw => lastSentence.includes(kw))) {
        recommendation = lastSentence;
        detailSentences = sentences.slice(1, -1).filter(s => s.trim());
      } else {
        detailSentences = sentences.slice(1).filter(s => s.trim());
      }
    }

    const isStrength = type === 'strength';

    return (
      <div className="space-y-2">
        {/* 핵심 요약 */}
        <p className="text-[12px] font-semibold text-headerBg">{summary}</p>
        {/* 상세 설명 - 불릿 리스트 */}
        {detailSentences.length > 0 && (
          <ul className="space-y-1 pl-3">
            {detailSentences.map((sentence, i) => (
              <li key={i} className="flex items-start gap-1.5 text-[12px] leading-relaxed text-gray-500">
                <span className={`mt-1.5 size-1.5 shrink-0 rounded-full ${isStrength ? 'bg-emerald-300' : 'bg-red-300'}`} />
                {sentence}
              </li>
            ))}
          </ul>
        )}
        {/* 추천 학습법 */}
        {recommendation && (
          <div className={`flex items-start gap-1.5 rounded-xl ${isStrength ? 'bg-emerald-50' : 'bg-red-50'} px-3 py-2`}>
            <Lightbulb size={13} className={`mt-0.5 shrink-0 ${isStrength ? 'text-emerald-500' : 'text-red-400'}`} />
            <p className={`text-[11px] leading-relaxed ${isStrength ? 'text-emerald-700' : 'text-red-600'}`}>{recommendation}</p>
          </div>
        )}
      </div>
    );
  }, []);

  // 텍스트 내 '따옴표' 키워드를 인라인 뱃지로 강조
  const renderHighlightedText = useCallback((text) => {
    const parts = text.split(/('.*?')/g);
    return parts.map((part, i) => {
      if (part.startsWith("'") && part.endsWith("'")) {
        const keyword = part.slice(1, -1);
        return <span key={i} className="inline-block rounded-full bg-green-50/50 px-1.5 py-0.5 text-[11px] font-bold text-primary">{keyword}</span>;
      }
      return <span key={i}>{part}</span>;
    });
  }, []);

  // 공부가 필요한 영역 데이터 가져오기
  const getStudyAreas = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.opinions || !reportData.data.opinions.next_step) {
      return [];
    }

    const apiStudyAreas = Object.entries(reportData.data.opinions.next_step).map(([title, topics]) => ({
      title,
      topics: Array.isArray(topics) ? topics : topics.split('\n').filter(t => t.trim())
    }));

    return apiStudyAreas;
  }, [reportData]);

  // 학습 조언 데이터 가져오기
  const getLearningGoals = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.opinions) {
      return {
        goals: [],
        overallComment: ""
      };
    }

    // comparison을 목표로 변환
    const comparison = reportData.data.opinions.comparison || {};
    const goals = [];
    
    // 기간에 따라 목표 분리
    const comparisonEntries = Object.entries(comparison);
    
    if (comparisonEntries.length >= 1 && comparisonEntries[0][1]) {
      goals.push({
        title: comparisonEntries[0][0] || '단기 목표',
        description: comparisonEntries[0][1]
      });
    }
    
    if (comparisonEntries.length >= 2 && comparisonEntries[1][1]) {
      goals.push({
        title: comparisonEntries[1][0] || '중기 목표',
        description: comparisonEntries[1][1]
      });
    }
    
    if (comparisonEntries.length >= 3 && comparisonEntries[2][1]) {
      goals.push({
        title: comparisonEntries[2][0] || '장기 목표',
        description: comparisonEntries[2][1]
      });
    }
    
    // 종합 코멘트
    const apiOverallComment = reportData.data.opinions.summary || "";

    return {
      goals,
      overallComment: apiOverallComment
    };
  }, [reportData]);

  // Populate UI state from API data
  useEffect(() => {
    if (reportData && reportData.data) {
      try {
        // 학생 정보 업데이트
        if (reportData.data.student_info) {
          setStudentInfo(reportData.data.student_info);
        }
        
        // 시험 정보 업데이트
        if (reportData.data.exams && reportData.data.exams.length > 0) {
          const latestExam = reportData.data.exams[0];
          setExamInfo({
            exam_type: latestExam.exam_type || '',
            updated_at: latestExam.updated_at || '',
            exam_count: latestExam.exam_round || 0,
            difficulty: latestExam.difficulty || 0,
            length: latestExam.length || 0,
            exam_id: latestExam.exam_id || ''
          });
        }
        
        // 과목 정보 업데이트
        if (reportData.data.tree_name) {
          setFieldInfo({
            tree_name: reportData.data.tree_name || ''
          });
        }
        
        // 레이팅 데이터 업데이트
        setRatingData(getRatingData());
        
      } catch (err) {
        console.error("Error processing API data:", err);
      }
    }
  }, [reportData, getRatingData]);
  
  // --- API 데이터 처리 함수 추가 끝 ---

  const fetchReport = async () => {
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;
    if (!stampId || !examId) return;

    setReportLoading(true);
    setReportError(null);
    // Do not reset reportData here initially

    try {
      const token = await getAccessToken();
      if (!token) throw new Error("인증 토큰 없음");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        // *** Set reportData to null for 404 ***
        setReportData(null);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.status === 'success') {
        setReportData(data);
        
        // 학생 정보 설정
        if (data.data?.student_info) {
          setStudentInfo(data.data.student_info);
        }
        
        // 시험 정보 직접 설정
        if (data.data.exams && data.data.exams.length > 0) {
          const latestExam = data.data.exams[0];
          setExamInfo({
            exam_type: latestExam.exam_type || '',
            updated_at: latestExam.updated_at || '',
            exam_count: latestExam.exam_round || 0,
            difficulty: latestExam.difficulty || 0,
            length: latestExam.length || 0,
            exam_id: latestExam.exam_id || ''
          });
        }
        
        // 과목 정보 직접 설정
        if (data.data.tree_name) {
          setFieldInfo({
            tree_name: data.data.tree_name || ''
          });
        }
      } else {
        setReportData(data); // Store even if internal status indicates issue
        setReportError(data.message || '리포트 로드 실패');
      }
    } catch (error) {
      setReportError(error.message);
    } finally {
      setReportLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    // Use consistent variable names from examData
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;

    if (!stampId || !examId) {
      setReportError("리포트 생성에 필요한 정보(stamp_id 또는 exam_id)가 없습니다.");
      return;
    }

    setCreateLoading(true);
    setReportError(null); // Clear previous errors

    try {
      const token = await getAccessToken();
      // Use correct endpoint with stamp_id and exam_id in the URL path
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json', // Keep Content-Type even if body is empty
        },
        // Remove body as per API spec
        // body: JSON.stringify({ ... }), 
      });

      // Check for specific error codes first
      if (response.status === 403) {
        throw new Error('리포트를 생성할 권한이 없습니다. (강사 권한 제한)');
      }
      if (response.status === 409) {
        throw new Error('리포트를 생성할 수 없습니다. (시험 미종료 또는 리포트 이미 존재)');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `리포트 생성 요청 실패: ${response.statusText}`);
      }

      // Check for 201 Created status code for success
      if (response.status === 201) {
        const data = await response.json(); // Get success message if needed
        // Set a temporary status or immediately refetch
        setReportData({ data: { status: 101, message: '리포트 생성 요청 성공. 생성 중...' } }); // Assume 101 for Creating
        // Refetch after a delay to check progress
        setTimeout(() => fetchReport(), 3000);
      } else {
        // Handle other potential success statuses if needed, otherwise treat as error
        const data = await response.json().catch(() => ({}));
        setReportError(data.message || `알 수 없는 성공 응답: ${response.status}`);
        setReportData(data); // Store response even if status is not ideal
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setReportError(error.message || '리포트 생성 중 오류가 발생했습니다.');
    } finally {
      setCreateLoading(false); // Stop loading indicator after attempt
    }
  };

  // --- NEW: Delete Report Handler ---
  const handleDeleteReport = async () => {
    // Use consistent variable names from examData
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;

    if (!stampId || !examId) {
      setReportError("리포트 삭제에 필요한 정보(stamp_id 또는 exam_id)가 없습니다.");
      return;
    }

    // Confirmation step
    if (!window.confirm('정말로 이 리포트를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.')) {
      return;
    }

    setDeleteLoading(true);
    setReportError(null); // Clear previous errors

    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          // No Content-Type needed for DELETE with no body
        },
      });

      // Check for specific error codes first
      if (response.status === 403) {
        throw new Error('리포트를 삭제할 권한이 없습니다. (강사 권한 제한)');
      }
      if (response.status === 409) {
        throw new Error('리포트를 삭제할 수 없습니다. (시험 미종료 또는 진행 중 상태)');
      }
      if (response.status === 404) {
        throw new Error('삭제할 리포트를 찾을 수 없습니다.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `리포트 삭제 요청 실패: ${response.statusText}`);
      }

      // Check for 200 OK status code for success
      if (response.status === 200) {
        // *** Set reportData to null to trigger the 'Create' button view ***
        setReportData(null);
        console.log('Report deleted successfully', await response.json());
      } else {
        const data = await response.json().catch(() => ({}));
        setReportError(data.message || `알 수 없는 삭제 응답: ${response.status}`);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      setReportError(error.message || '리포트 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteLoading(false); // Stop loading indicator after attempt
    }
  };

  // --- NEW: Retry Report Handler ---
  const handleRetryReport = () => {
    // 메모 입력 모달 표시
    setMemoVisible(true);
  };

  // --- NEW: Confirm Retry with Memo Handler ---
  const handleConfirmRetryReport = async () => {
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;
    if (!stampId || !examId) return;

    setRetryLoading(true);
    setReportError(null);
    setMemoVisible(false); // 모달 닫기

    try {
      const token = await getAccessToken();
      
      // 1단계: 메모 저장 API 호출
      const memoResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report/modify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ memo: retryMemo }), // 메모 데이터 전송
      });

      if (memoResponse.status === 400) throw new Error('메모가 너무 길거나 잘못된 요청입니다.');
      if (memoResponse.status === 403) throw new Error('리포트 수정 권한이 없습니다.');
      if (!memoResponse.ok) {
        const errorData = await memoResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `메모 저장 요청 실패: ${memoResponse.statusText}`);
      }

      // 메모 저장 성공 후 2단계: 재생성 API 호출
      const retryResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report/retry`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (retryResponse.status === 403) throw new Error('리포트 재생성 권한이 없습니다.');
      if (retryResponse.status === 409) throw new Error('재생성 불가 상태입니다 (LLM 작업 중, 미종료 시험 또는 확정된 리포트).');
      
      if (!retryResponse.ok) {
        const errorData = await retryResponse.json().catch(() => ({}));
        throw new Error(errorData.message || `리포트 재생성 요청 실패: ${retryResponse.statusText}`);
      }
      
      if (retryResponse.status === 200) {
        console.log('Report retry submitted successfully', await retryResponse.json());
        setRetryMemo(''); // 메모 초기화
        fetchReport(); // 리포트 상태 새로고침
      }
    } catch (error) {
      setReportError(error.message);
    } finally {
      setRetryLoading(false);
    }
  };

  // --- NEW: Confirm Report Handler ---
  const handleConfirmReport = async () => {
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;
    if (!stampId || !examId) return;

    if (!window.confirm('리포트를 확정하시겠습니까? 확정 후에는 일부 수정이 제한될 수 있습니다.')) return;

    setConfirmLoading(true);
    setReportError(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report/confirm`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.status === 403) throw new Error('리포트 확정 권한이 없습니다.');
      if (response.status === 409) throw new Error('확정 불가 상태입니다 (LLM 작업 중, 미종료 시험 또는 이미 확정된 리포트).');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `리포트 확정 요청 실패: ${response.statusText}`);
      }
      if (response.status === 200) {
        console.log('Report confirmed successfully', await response.json());
        fetchReport(); // Refetch immediately to get status 999
      }
    } catch (error) {
      setReportError(error.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  // --- NEW: Re-open Report Handler ---
  const handleReopenReport = async () => {
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;
    if (!stampId || !examId) return;

    if (!window.confirm('리포트 확정 상태를 취소하시겠습니까?')) return;

    setReopenLoading(true);
    setReportError(null);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report/re-open`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.status === 403) throw new Error('확정 취소 권한이 없습니다.');
      if (response.status === 409) throw new Error('확정 취소 불가 상태입니다 (LLM 작업 중, 미종료 시험 또는 미확정 리포트).');
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `확정 취소 요청 실패: ${response.statusText}`);
      }
      if (response.status === 200) {
        console.log('Report re-opened successfully', await response.json());
        fetchReport(); // Refetch immediately to get status 200/998
      }
    } catch (error) {
      setReportError(error.message);
    } finally {
      setReopenLoading(false);
    }
  };

  // Call fetchReport when modal opens or examData changes
  useEffect(() => {
    if (isOpen && examData) {
      fetchReport();
    } else {
      setReportData(null);
      setReportLoading(false);
      setReportError(null);
      setCreateLoading(false);
      setDeleteLoading(false);
      // --- Reset new loading states --- 
      setRetryLoading(false);
      setConfirmLoading(false);
      setReopenLoading(false);
      // Reset edit modes
      setIsEditingStrengths(false); setTempStrengths(null);
      setIsEditingWeaknesses(false); setTempWeaknesses(null);
      setIsEditingStudyAreas(false); setTempStudyAreas(null);
      setIsEditingShortTermGoals(false); setTempShortTermGoals(null);
      setIsEditingMidTermGoals(false); setTempMidTermGoals(null);
      setIsEditingLongTermGoals(false); setTempLongTermGoals(null);
      setIsEditingOverallComment(false); setTempOverallComment('');
      // 학습 조언 전체 수정 상태 초기화
      setIsEditingLearningGoals(false); setTempLearningGoals(null);
      
      // 학생 정보 초기화
      setStudentInfo(null);
      // UI 데이터 상태 초기화
      setStrengths([]);
      setWeaknesses([]);
      setStudyAreas([]);
      setShortTermGoals({ period: '', goals: '' });
      setMidTermGoals({ period: '', goals: '' });
      setLongTermGoals({ period: '', goals: '' });
      setOverallComment('');
      // 레이팅 데이터 초기화
      setRatingData([]);
      // 시험/필드 정보 초기화
      setExamInfo({
        exam_type: '',
        updated_at: '',
        exam_count: 0,
        difficulty: 0,
        length: 0,
        exam_id: ''
      });
      setFieldInfo({
        tree_name: ''
      });
    }
  }, [isOpen, examData]);

  // Remove the original dummy loading useEffect
  /*
  useEffect(() => {
    // ... original dummy loading logic ...
  }, [isOpen]);
  */

  const formatExamDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getFullYear().toString().slice(2);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch (e) {
      return '';
    }
  };

  const getBadgeColor = (percentage) => {
    if (percentage === null) return 'bg-gray-400';
    if (percentage >= 75) return 'bg-primary';
    if (percentage < 50) return 'bg-danger';
    return 'bg-yellow-500';
  };

  // --- Handlers for temporary data changes ---
  const handleTempStrengthChange = (index, value) => {
    const updated = [...tempStrengths];
    updated[index].description = value;
    setTempStrengths(updated);
  };

  const handleTempWeaknessChange = (index, value) => {
    const updated = [...tempWeaknesses];
    updated[index].description = value;
    setTempWeaknesses(updated);
  };

  const handleCancelStrengths = () => {
    setIsEditingStrengths(false);
    setTempStrengths(null);
  };

  const handleCancelWeaknesses = () => {
    setIsEditingWeaknesses(false);
    setTempWeaknesses(null);
  };

  const handleSaveStudyAreas = () => {
    setStudyAreas(tempStudyAreas);
    setIsEditingStudyAreas(false);
    setTempStudyAreas(null);
  };
  const handleCancelStudyAreas = () => {
    setIsEditingStudyAreas(false);
    setTempStudyAreas(null);
  };

  const handleSaveShortTermGoals = () => {
    setShortTermGoals(prevState => ({ ...prevState, goals: tempShortTermGoals }));
    setIsEditingShortTermGoals(false);
    setTempShortTermGoals(null);
  };
  const handleCancelShortTermGoals = () => {
    setIsEditingShortTermGoals(false);
    setTempShortTermGoals(null);
  };

  const handleSaveMidTermGoals = () => {
    setMidTermGoals(prevState => ({ ...prevState, goals: tempMidTermGoals }));
    setIsEditingMidTermGoals(false);
    setTempMidTermGoals(null);
  };
  const handleCancelMidTermGoals = () => {
    setIsEditingMidTermGoals(false);
    setTempMidTermGoals(null);
  };

  const handleSaveLongTermGoals = () => {
    setLongTermGoals(prevState => ({ ...prevState, goals: tempLongTermGoals }));
    setIsEditingLongTermGoals(false);
    setTempLongTermGoals(null);
  };
  const handleCancelLongTermGoals = () => {
    setIsEditingLongTermGoals(false);
    setTempLongTermGoals(null);
  };

  const handleCancelOverallComment = () => {
    setIsEditingOverallComment(false);
  };

  // 2. handlePrint 함수 구현 (새 창 + 스타일/콘텐츠 복사 방식)
  const handlePrint = () => {
    const printContentEl = document.getElementById('reportContent');
    if (!printContentEl) {
      console.error('인쇄할 콘텐츠 요소를 찾을 수 없습니다. (ID: reportContent)');
      return;
    }

    const printWindow = window.open('', '_blank', 'width=1000,height=800,scrollbars=yes');

    if (!printWindow) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제하고 다시 시도해주세요.');
      return;
    }

    const contentHTML = printContentEl.innerHTML;
    const originalDocument = document;
    let stylesHTML = '';

    // 모든 스타일시트 (link 및 style 태그) 복사
    Array.from(originalDocument.styleSheets).forEach(sheet => {
      try {
        if (sheet.href) { // <link> 태그
          // CORS 문제 방지를 위해 절대 경로 사용 고려 (필요한 경우)
          stylesHTML += `<link rel="stylesheet" href="${sheet.href}">\n`;
        } else if (sheet.cssRules) { // <style> 태그
            stylesHTML += `<style>${Array.from(sheet.cssRules).map(rule => rule.cssText).join('\n')}</style>\n`;
        }
      } catch (e) {
        // 보안 제약 등으로 접근 불가한 스타일 시트는 건너뜀
        console.warn('스타일시트 복사 중 오류:', sheet, e);
      }
    });
    
    // 추가적인 인쇄 전용 스타일 (필요시)
    stylesHTML += `
      <style>
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            margin: 0;
            padding: 0;
            font-family: Arial, sans-serif; /* 기본 폰트 */
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          /* 인쇄 시 필요 없는 요소 숨김 (새 창 기준이므로 .no-print 적용은 콘텐츠 내부에 있어야 함) */
          .no-print {
            display: none !important;
          }
          /* 페이지 나누기 클래스 */
          .page-break-before {
            page-break-before: always;
            margin-top: 50px; /* 위쪽 여백 추가 */
          }
          /* 차트 크기 조정 등 필요한 스타일 추가 */
          /* 예: .recharts-wrapper { width: 100% !important; height: auto !important; } */
        }
      </style>
    `;

    // 새 창에 HTML 구조 작성
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>카이리포트 인쇄</title>
          ${stylesHTML}
        </head>
        <body>
          ${contentHTML}
          <script>
            // 이미지 및 스타일 로드 후 인쇄 시도
            window.addEventListener('load', function() {
              setTimeout(function() { // DOM 렌더링 시간 추가 확보
                console.log('Print triggered by load event');
                window.printed = true; // 인쇄 시작 플래그 설정
                window.print();
                // window.close(); 
              }, 500); // 0.5초 지연
            });
            // load 이벤트가 불안정할 경우 대비 (긴 지연 시간)
            setTimeout(function() { 
              if (!window.printed) { // 인쇄 플래그 확인
                console.log('Fallback print triggered');
                window.printed = true; // 여기서도 플래그 설정
                window.print(); 
                // window.close(); 
              }
            }, 2000); // 2초 지연
          </script>
        </body>
      </html>
    `);

    printWindow.document.close(); // 스트림 닫기 (렌더링 시작)
  };

  // 난이도별 레이더 차트 데이터 함수 수정
  const levelRadarData = useCallback(() => {
    if (!reportData || !reportData.data || !reportData.data.recent_record || !reportData.data.past_record) {
      return [1, 2, 3, 4, 5].map(level => ({
        subject: `Lv ${level}`,
        level,
        A: 0,
        fullMark: 100,
      }));
    }

    // 현재 시험 데이터와 누적 시험 데이터 합치기
    const recentCorrect = reportData.data.recent_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const recentIncorrect = reportData.data.recent_record.by_step?.[1] || [0, 0, 0, 0, 0];
    const pastCorrect = reportData.data.past_record.by_step?.[0] || [0, 0, 0, 0, 0];
    const pastIncorrect = reportData.data.past_record.by_step?.[1] || [0, 0, 0, 0, 0];

    // 각 난이도별 맞은 문제 수와 틀린 문제 수를 합산
    const totalCorrect = recentCorrect.map((count, i) => count + pastCorrect[i]);
    const totalIncorrect = recentIncorrect.map((count, i) => count + pastIncorrect[i]);

    // 각 난이도별 정답률 계산 - 필터 제거하여 항상 5개 레벨 모두 표시
    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = totalCorrect[index] || 0;
      const incorrect = totalIncorrect[index] || 0;
      const total = correct + incorrect;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      return {
        subject: `Lv ${level}`,
        level,
        A: accuracy,
        fullMark: 100,
      };
    });
  }, [reportData]);

  // domainLegend 동적 생성 함수 추가
  const getDomainLegend = useCallback(() => {
    const domainData = getDomainDonutData();
    if (domainData.length === 0) return [];
    
    return domainData
    // .slice(0, 2)
    .map((item, index) => ({
      name: item.name,
      color: DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length]
    }));
  }, [getDomainDonutData]);

  // 새로운 API 통신 함수 추가 - opinions 수정
  const handleUpdateReport = async (updatedData) => {
    const stampId = examData?.stamp_id;
    const examId = examData?.exam_id;
    if (!stampId || !examId) {
      setReportError("리포트 수정에 필요한 정보(stamp_id 또는 exam_id)가 없습니다.");
      return false;
    }

    // 수정 중 로딩 상태 설정
    setReportLoading(true);
    setReportError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/report/modify`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatedData)
      });

      // 오류 처리
      if (response.status === 403) throw new Error('리포트를 수정할 권한이 없습니다. (강사 권한 제한)');
      if (response.status === 409) throw new Error('수정 불가 상태입니다 (LLM 작업 중, 미종료 시험 또는 확정된 리포트).');
      if (response.status === 404) throw new Error('수정할 리포트를 찾을 수 없습니다.');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `리포트 수정 요청 실패: ${response.statusText}`);
      }

      // 성공 처리
      if (response.status === 200) {
        const data = await response.json();
        console.log('Report updated successfully', data);
        // 리포트 데이터 다시 불러오기
        fetchReport();
        return true;
      }
    } catch (error) {
      console.error('Error updating report:', error);
      setReportError(error.message || '리포트 수정 중 오류가 발생했습니다.');
      return false;
    } finally {
      setReportLoading(false);
    }
    return false;
  };
  
  // --- 강점 수정 핸들러 ---
  const handleEditStrengths = () => {
    // API 데이터에서 원본 데이터를 가져옴
    const { strengths } = getStrengthsAndWeaknesses();
    setIsEditingStrengths(true);
    setTempStrengths(strengths);
  };

  // --- 약점 수정 핸들러 ---
  const handleEditWeaknesses = () => {
    // API 데이터에서 원본 데이터를 가져옴
    const { weaknesses } = getStrengthsAndWeaknesses();
    setIsEditingWeaknesses(true);
    setTempWeaknesses(weaknesses);
  };

  // --- 강점 저장 핸들러 수정 ---
  const handleSaveStrengths = async () => {
    // 강점 목록을 서버 형식(pros)으로 변환
    const prosText = tempStrengths.map(item => item.description).join('\n\n');
    
    // 현재 약점도 함께 보내기 위해 가져옴
    const consText = reportData?.data?.opinions?.cons || '';
    
    // API 호출하여 저장
    const success = await handleUpdateReport({
      opinions: {
        ...reportData?.data?.opinions,
        pros: prosText,
        cons: consText
      }
    });
    
    if (success) {
      // UI 상태 업데이트는 fetchReport 후 useEffect에서 자동으로 처리됨
      setIsEditingStrengths(false);
      setTempStrengths(null);
    }
  };

  // --- 약점 저장 핸들러 수정 ---
  const handleSaveWeaknesses = async () => {
    // 약점 목록을 서버 형식(cons)으로 변환
    const consText = tempWeaknesses.map(item => item.description).join('\n\n');
    
    // 현재 강점도 함께 보내기 위해 가져옴
    const prosText = reportData?.data?.opinions?.pros || '';
    
    // API 호출하여 저장
    const success = await handleUpdateReport({
      opinions: {
        ...reportData?.data?.opinions,
        pros: prosText,
        cons: consText
      }
    });
    
    if (success) {
      // UI 상태 업데이트는 fetchReport 후 useEffect에서 자동으로 처리됨
      setIsEditingWeaknesses(false);
      setTempWeaknesses(null);
    }
  };

  // 학습 조언 수정 핸들러
  const handleEditLearningGoals = () => {
    const { goals, overallComment } = getLearningGoals();
    // 목표 데이터가 있는 경우에만 설정
    if (goals.length > 0) {
      setTempLearningGoals(goals.map(goal => ({ ...goal })));
    } else {
      // 기본 데이터 구조 설정
      setTempLearningGoals([
        { title: '단기 목표', description: '' },
        { title: '중기 목표', description: '' },
        { title: '장기 목표', description: '' }
      ]);
    }
    setIsEditingLearningGoals(true);
  };

  const handleTempLearningGoalChange = (index, value) => {
    const updated = [...tempLearningGoals];
    updated[index].description = value;
    setTempLearningGoals(updated);
  };

  const handleCancelLearningGoals = () => {
    setIsEditingLearningGoals(false);
    setTempLearningGoals(null);
  };

  const handleSaveLearningGoals = async () => {
    if (!tempLearningGoals) return;

    // 서버 형식으로 변환
    const comparison = {};
    tempLearningGoals.forEach(goal => {
      comparison[goal.title] = goal.description;
    });

    // 현재 다른 opinions 데이터도 함께 보존
    const currentOpinions = reportData?.data?.opinions || {};
    
    const success = await handleUpdateReport({
      opinions: {
        ...currentOpinions,
        comparison
      }
    });

    if (success) {
      setIsEditingLearningGoals(false);
      setTempLearningGoals(null);
    }
  };

  // 종합 평가 수정 핸들러
  const handleEditOverallComment = () => {
    const { overallComment } = getLearningGoals();
    setIsEditingOverallComment(true);
    setTempOverallComment(overallComment || '');
  };

  // 종합 평가 저장 핸들러
  const handleSaveOverallComment = async () => {
    // 현재 다른 opinions 데이터도 함께 보존
    const currentOpinions = reportData?.data?.opinions || {};
    
    const success = await handleUpdateReport({
      opinions: {
        ...currentOpinions,
        summary: tempOverallComment
      }
    });

    if (success) {
      setIsEditingOverallComment(false);
      // tempOverallComment는 초기화하지 않음 (필요시 재사용)
    }
  };

  if (!isOpen) return null;

  const isMobileView = window.innerWidth < 768;

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center" 
      style={{ 
        zIndex: 9999980,
        paddingTop: isMobileView ? 'env(safe-area-inset-top, 0px)' : '0px',
        paddingBottom: isMobileView ? 'calc(68px + env(safe-area-inset-bottom, 0px))' : '0px'
      }}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>
      
      {/* 메모 입력 모달 */}
      {memoVisible && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setMemoVisible(false)}></div>
          <div className="relative z-10 mx-4 w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
            <h2 className="mb-3 text-lg font-semibold">재생성 요청 프롬프트 (선택 사항)</h2>
            <p className="mb-4 text-sm text-gray-600">재생성 시 LLM이 참고할 내용을 입력해주세요. (최대 500자)</p>
            <textarea
              className="w-full resize-none rounded-3xl border border-gray-300 p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
              rows={5}
              maxLength={500}
              value={retryMemo}
              onChange={(e) => setRetryMemo(e.target.value)}
              placeholder="예: 강점에 긍정적인 부분을 좀 더 추가해주세요"
            />
            <div className="mt-5 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setMemoVisible(false);
                  setRetryMemo(''); // 취소 시 메모 초기화
                }}
                className="rounded-3xl bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                disabled={retryLoading}
              >
                취소
              </button>
              <button
                onClick={handleConfirmRetryReport}
                className={`rounded-3xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${retryLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                disabled={retryLoading}
              >
                {retryLoading ? '요청 중...' : '요청'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div 
        className="relative z-10 flex w-[95vw] flex-col overflow-hidden rounded-3xl bg-white shadow-lg lg:w-[960px]"
        style={{
          height: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh',
          maxHeight: isMobileView ? 'calc((100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)) * 0.75)' : '90vh'
        }}
      >
        {/* 3. 헤더에 no-print 클래스 추가 및 인쇄 버튼 추가 */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-3xl border-b bg-white p-4">
          <div className="flex items-center">
            <h1 className="text-xl font-bold text-gray-800">AI 리포트</h1>
          </div>
          <div className="flex items-center"> {/* 버튼 그룹 div */}
            <button onClick={handlePrint} className="mr-2 rounded-full p-1 hover:bg-gray-200">
              <Printer size={20} />
            </button>
            <button onClick={onClose} className="rounded-full border border-gray-300 p-1 hover:bg-gray-200">
              <X size={24} />
            </button>
          </div>
        </div>

        {/* 4. 컨텐츠 영역에 id와 ref 추가 */}
        <div 
          className="overflow-y-auto p-4" 
          id="reportContent" 
          ref={reportContentRef}
          style={{
            flex: '1 1 0',
            minHeight: 0,
            height: 'calc(100% - 80px)', // 헤더 높이(약 80px) 제외
            maxHeight: 'calc(100% - 80px)'
          }}
        >
          {/* --- NEW: API Status Display Area --- */}
          {/* 이 div에 no-print 클래스 추가 */}
          <div className="no-print mb-4 rounded-3xl border bg-gray-50 p-3 text-sm">
            {reportLoading ? (
              <div className="flex items-center gap-2 text-gray-600">
                <div className="size-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent"></div>
                <span>리포트 상태 확인 중...</span>
            </div>
            ) : reportError ? (
              <div className="text-red-600">
                <span className="font-semibold">오류:</span> {reportError}
                <button
                  onClick={() => fetchReport()}
                  className="ml-2 rounded bg-red-100 px-2 py-0.5 text-xs text-red-700 hover:bg-red-200"
                >
                  재시도
                </button>
            </div>
            ) : !reportData ? (
              // *** Case 1: reportData is null (initial or 404) -> Show Create Button ***
              <div className="flex items-center justify-between">
                <span className="text-blue-700">리포트가 없습니다.</span>
                  <button
                  onClick={handleGenerateReport}
                  disabled={createLoading}
                  className={`rounded px-3 py-1 text-xs text-white ${createLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'
                    }`}
                >
                  {createLoading ? '요청 중...' : '생성'}
                  </button>
              </div>
            ) : (
              // *** Case 2: reportData exists -> Show buttons based on internal status ***
              <>
                {/* Subcase 2.1: Confirmed (status 999) -> Reopen, Delete */}
                {reportData.data?.status === 999 && (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 size={16} />
                      <span>리포트 확정 완료</span>
                    </div>
                    <div className="flex gap-2">
                  <button
                        onClick={handleReopenReport}
                        disabled={reopenLoading || deleteLoading}
                        className={`rounded px-3 py-1 text-xs text-white ${reopenLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-yellow-500 hover:bg-yellow-600'
                          }`}
                      >
                        {reopenLoading ? '요청 중...' : '확정취소'}
                  </button>
                  <button
                        onClick={handleDeleteReport}
                        disabled={reopenLoading || deleteLoading}
                        className={`rounded-3xl px-3 py-1 text-xs text-white ${deleteLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-red-500 hover:bg-red-600'
                          }`}
                      >
                        {deleteLoading ? '요청 중...' : '삭제'}
                  </button>
                </div>
              </div>
                )}

                {/* Subcase 2.2: Not Confirmed (status 100, 101, 102, 150, 200, 998) -> Retry, Confirm, Delete */}
                {[100, 101, 102, 150, 200, 998, 702, 704, 500].includes(reportData.data?.status) && (
                  <div className="flex items-center justify-between gap-2">
                    {/* Informational Status Display */}
                    <div className="flex items-center gap-2">
                      {reportData.data?.status === 101 && (
                        <><div className="size-4 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent"></div><span className="text-yellow-700">생성 중...</span></>
                      )}
                      {reportData.data?.status === 102 && (
                        <><XCircle size={16} className="text-red-500" /><span className="text-red-700">생성 오류: {reportData.data?.message || ''}</span></>
                      )}
                      {(reportData.data?.status === 200 || reportData.data?.status === 998) && (
                        <><CheckCircle2 size={16} className="text-blue-500" /><span className="text-blue-600">준비 완료 (미확정)</span></>
                      )}
                      {reportData.data?.status === 100 && (
                        <><Clock size={16} className="text-gray-500" /><span className="text-gray-600">생성 요청됨</span></>
                      )}
                      {reportData.data?.status === 150 && (
                        <><div className="size-4 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent"></div><span className="text-yellow-700">편집 중...</span></>
                      )}
                      {reportData.data?.status === 702 && (
                        <><XCircle size={16} className="text-orange-500" /><span className="text-orange-700">702 상태: 처리 가능</span></>
                      )}
                      {reportData.data?.status === 704 && (
                        <><XCircle size={16} className="text-orange-500" /><span className="text-orange-700">704 상태: 처리 가능</span></>
                      )}
                      {reportData.data?.status === 500 && (
                        <><XCircle size={16} className="text-red-500" /><span className="text-red-700">서버 오류: 재시도가 필요합니다</span></>
                      )}
                    </div>

                    {/* Action Buttons: Retry, Confirm, Delete */}
                    <div className="flex gap-2">
                      {/* Show Refresh button when status is 100, 101, or 150 */}
                      {(reportData.data?.status === 100 || reportData.data?.status === 101 || reportData.data?.status === 150) && (
                        <button
                          onClick={() => fetchReport()}
                          disabled={reportLoading}
                          className="rounded-3xl bg-gray-200 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-300"
                        >
                          새로고침
                        </button>
                      )}
                      
                      {/* Show retry button only when status is 102, 200, 702, 704, 500, 998 */}
                      {(reportData.data?.status === 102 || reportData.data?.status === 200 || reportData.data?.status === 702 || reportData.data?.status === 704 || reportData.data?.status === 500 || reportData.data?.status === 998) && (
                        <button
                          onClick={handleRetryReport}
                          disabled={retryLoading || confirmLoading || deleteLoading || reopenLoading || createLoading}
                          className={`rounded-3xl px-3 py-1 text-xs text-white ${retryLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-yellow-500 hover:bg-yellow-600'
                            }`}
                        >
                          {retryLoading ? '요청 중...' : ((reportData.data?.status === 702 || reportData.data?.status === 704) ? '재생성' : (reportData.data?.status === 102 ? '생성 재시도' : '재생성'))}
                        </button>
                      )}
                      
                      {/* 702, 704, 500 상태에서는 확정 버튼 비활성화 */}
                      {reportData.data?.status !== 702 && reportData.data?.status !== 704 && reportData.data?.status !== 500 && (
                        <button
                          onClick={handleConfirmReport}
                          disabled={retryLoading || confirmLoading || deleteLoading || reopenLoading || createLoading}
                          className={`rounded-3xl px-3 py-1 text-xs text-white ${confirmLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-green-500 hover:bg-green-600'
                            }`}
                        >
                          {confirmLoading ? '요청 중...' : '확정'}
                        </button>
                      )}
                      <button
                        onClick={handleDeleteReport}
                        disabled={retryLoading || confirmLoading || deleteLoading || reopenLoading || createLoading}
                        className={`rounded-3xl px-3 py-1 text-xs text-white ${deleteLoading ? 'cursor-not-allowed bg-gray-400' : 'bg-red-500 hover:bg-red-600'
                          }`}
                      >
                        {deleteLoading ? '요청 중...' : '삭제'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Fallback for any truly unknown statuses from the API */}
                {![100, 101, 102, 150, 200, 998, 999, 702, 704, 500].includes(reportData.data?.status) && (
                  <div className="text-gray-600">알 수 없는 리포트 상태: {reportData.data?.status}</div>
                )}
                </>
              )}
          </div>

          {/* 시험 정보 카드와 학생 정보 카드를 나란히 배치하는 컨테이너 */}
          <div className="mb-[20px] flex flex-wrap gap-4">

            {/* 학생 정보 카드 */}
            <div className="page-break-before min-w-[300px] flex-1 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)]">
              {/* <h3 className="text-[14px] font-bold mb-3 text-headerBg">학생 정보</h3> */}
              
              {studentInfo ? (
                <div className="flex h-[82px] w-full flex-col justify-center rounded-[16px] bg-gray-100 px-4 py-3">
                  <div className="grid grid-cols-[80px_1fr] items-center gap-y-2">
                    <span className="text-[11px] text-headerBg/60">이름:</span>
                    <span className="text-[13px] font-medium text-headerBg">{studentInfo.student_name}</span>
                    
                    <span className="text-[11px] text-headerBg/60">학교:</span>
                    <span className="text-[13px] font-medium text-headerBg">{studentInfo.school_name} {studentInfo.school_grade}{!isNaN(studentInfo.school_grade) && typeof studentInfo.school_grade !== 'boolean' ? "학년" : ""}</span>
                    
                    {/* <span className="text-headerBg/60 text-[11px]">학과/학년:</span> */}
                    {/* <span className="text-headerBg font-medium text-[13px]">{studentInfo.school_grade}</span> */}
                  </div>
                </div>
              ) : (
                <div className="flex h-[90px] w-full items-center justify-center rounded-[16px] bg-gray-100">
                  <p className="text-gray-500">학생 정보 로딩 중...</p>
                </div>
              )}
            </div>

            {/* 시험 정보 카드 */}
            <div className="page-break-before min-w-[300px] flex-1 rounded-3xl p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)]">
              {/* <h3 className="text-[14px] font-bold mb-3 text-headerBg">시험 정보</h3> */}
              {examInfo && fieldInfo ? (
                <div className="flex flex-col">
                  <div className="mb-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] font-semibold text-headerBg">
                        {fieldInfo.tree_name || '과목명'}
                      </span>
                      <span className="flex h-[16px] w-[38px] items-center justify-center rounded-full bg-primary text-[8px] text-white">
                        {examInfo.exam_type}
                      </span>
                    </div>
                    <span className="text-[12px] text-gray-500">
                      {formatExamDate(examInfo.updated_at)}
                    </span>
                  </div>

                  <div className="flex h-[50px] w-full items-center justify-center rounded-[16px] bg-gray-100 px-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-headerBg/60">회차</span>
                        <span className="text-[11px] text-headerBg">{examInfo.exam_count}</span>
                        <span className="text-[8px] text-headerBg/60">· TID</span>
                        <span className="text-[8px] text-headerBg">{examInfo.exam_id}</span>
                      </div>
                      <div className="h-3 w-px bg-gray-400"></div>
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-headerBg/60">난이도</span>
                        <span className="text-[11px] font-medium text-headerBg">{(examInfo.difficulty / 100).toFixed(2)}</span>
                      </div>
                      <div className="h-3 w-px bg-gray-400"></div>
                      <div className="flex items-center gap-1">
                        <span className="text-[11px] text-headerBg/60">문제 수</span>
                        <span className="text-[11px] font-medium text-headerBg">{examInfo.length}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-[72px] items-center justify-center">
                  <p className="text-gray-500">시험 정보 로딩 중...</p>
                </div>
              )}
            </div>
          </div>
          
          {/* 테스트 성적 요약 카드 시작 */}
          <div className="mb-8 w-full rounded-3xl bg-white p-6 shadow-[0_0_6px_rgba(0,0,0,0.1)]">
            <h2 className="mb-5 text-base font-bold text-headerBg">테스트 성적 요약</h2>

            {/* 레이팅, 정답률, 난이도 섹션 - 2열 그리드로 변경 */}
            <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* 항목 1: 현재 레이팅 (2열 차지) */}
              <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3 sm:col-span-2">
                <span className="mb-1 text-[14px] font-semibold text-headerBg">현재 폼레이팅</span>
                <p className="mb-2 text-[28px] font-black" style={{ color: getRatingColor(getCurrentRating()) }}>{getCurrentRating().toLocaleString()}</p>
                <div className="flex items-center">
                  <div className="flex h-[20px] items-center rounded-3xl border border-gray-300 px-2">
                  <Triangle
                        className={`mr-0.5 size-2 fill-current ${getCurrentRating() < 0
                            ? 'rotate-180 text-blue-600'
                            : 'rotate-0 text-red-600'
                          }`}
                  /> 
                    <div className="flex items-baseline">
                      <span className="text-[10px] font-semibold text-headerBg">{Math.abs(getRatingChange())}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 항목 2: 정답률 (현재 + 누적) */}
              <div className="flex flex-col rounded-3xl bg-gray-50 p-4">
                <span className="mb-3 text-center text-[14px] font-semibold text-headerBg">정답률</span>
                <div className="flex flex-col gap-3">
                  {/* 현재 정답률 */}
                  <div className="flex items-center gap-2">
                    <span className="w-8 shrink-0 text-[10px] font-semibold text-gray-400">현재</span>
                    <span className="w-10 shrink-0 text-[16px] font-bold text-headerBg">{getAccuracyData().accuracy}<span className="text-[11px]">%</span></span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${getAccuracyData().accuracy}%` }}></div>
                    </div>
                    <span className="w-14 shrink-0 rounded-3xl border border-gray-300 py-0.5 text-center text-[9px] font-bold"><span className="text-primary">{getAccuracyData().correctCount}</span><span className="text-gray-400">/</span><span className="text-headerBg">{getAccuracyData().totalCount}</span></span>
                  </div>
                  {/* 누적 정답률 */}
                  <div className="flex items-center gap-2">
                    <span className="w-8 shrink-0 text-[10px] font-semibold text-gray-400">누적</span>
                    <span className="w-10 shrink-0 text-[16px] font-bold text-headerBg">{getCumulativeAccuracyData().accuracy}<span className="text-[11px]">%</span></span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div className="absolute inset-y-0 left-0 rounded-full bg-headerBg/30" style={{ width: `${getCumulativeAccuracyData().accuracy}%` }}></div>
                    </div>
                    <span className="w-14 shrink-0 rounded-3xl border border-gray-300 py-0.5 text-center text-[9px] font-bold"><span className="text-primary">{getCumulativeAccuracyData().correctCount}</span><span className="text-gray-400">/</span><span className="text-headerBg">{getCumulativeAccuracyData().totalCount}</span></span>
                  </div>
                </div>
              </div>

              {/* 항목 3: 난이도 (평균 + 누적) */}
              <div className="flex flex-col rounded-3xl bg-gray-50 p-4">
                <span className="mb-3 text-center text-[14px] font-semibold text-headerBg">난이도</span>
                <div className="flex flex-col gap-3">
                  {/* 평균 난이도 */}
                  <div className="flex items-center gap-2">
                    <span className="w-8 shrink-0 text-[10px] font-semibold text-gray-400">현재</span>
                    <span className="w-10 shrink-0 text-[16px] font-bold" style={{ color: getDifficultyColor(getAverageDifficulty()) }}>{getAverageDifficulty().toFixed(1)}</span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div className="absolute inset-0 flex">
                        {[0, 1, 2, 3, 4].map((lv) => {
                          const seg = Math.min(Math.max(getAverageDifficulty() - lv, 0), 1);
                          if (seg <= 0) return null;
                          const colors = ['rgba(253,224,71,0.7)', 'rgba(134,239,172,0.7)', 'rgba(147,197,253,0.7)', 'rgba(253,186,116,0.7)', 'rgba(252,165,165,0.7)'];
                          return <div key={lv} style={{ width: `${(seg / 5) * 100}%`, backgroundColor: colors[lv] }} />;
                        })}
                      </div>
                    </div>
                    <span className="w-14 shrink-0 rounded-3xl border border-gray-300 py-0.5 text-center text-[9px] font-bold text-headerBg">{getDifficultyLevelText(getAverageDifficulty())}</span>
                  </div>
                  {/* 누적 난이도 */}
                  <div className="flex items-center gap-2">
                    <span className="w-8 shrink-0 text-[10px] font-semibold text-gray-400">누적</span>
                    <span className="w-10 shrink-0 text-[16px] font-bold" style={{ color: getDifficultyColor(getCumulativeDifficulty()) }}>{getCumulativeDifficulty().toFixed(1)}</span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <div className="absolute inset-0 flex">
                        {[0, 1, 2, 3, 4].map((lv) => {
                          const seg = Math.min(Math.max(getCumulativeDifficulty() - lv, 0), 1);
                          if (seg <= 0) return null;
                          const colors = ['rgba(253,224,71,0.7)', 'rgba(134,239,172,0.7)', 'rgba(147,197,253,0.7)', 'rgba(253,186,116,0.7)', 'rgba(252,165,165,0.7)'];
                          return <div key={lv} style={{ width: `${(seg / 5) * 100}%`, backgroundColor: colors[lv] }} />;
                        })}
                      </div>
                    </div>
                    <span className="w-14 shrink-0 rounded-3xl border border-gray-300 py-0.5 text-center text-[9px] font-bold text-headerBg">{getDifficultyLevelText(getCumulativeDifficulty())}</span>
                  </div>
                </div>
              </div>
            </div>

          {/* 나머지 그리드 레이아웃 */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            {/* 폼레이팅 변화 추이 (4칸 - 전체 가로 영역) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-4">
              <div>
                <h3 className="mb-3 text-[14px] font-bold text-headerBg">폼레이팅 변화 추이</h3>
                <div className="relative mb-2 h-44">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={getRatingData()}
                      margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id="ratingGradient" x1="0" y1="0" x2="0" y2="1">
                          {getRatingGradientStops().map((s, i) => (
                            <stop key={i} offset={`${s.offset * 100}%`} stopColor={s.color} stopOpacity={s.opacity} />
                          ))}
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        domain={getYAxisDomain()}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          const color = getRatingColorOpaque(payload.value);
                          return (
                            <text x={x} y={y} textAnchor="end" fontSize={8} fontWeight="bold" fill={color} dx={-2}>
                              {payload.value.toLocaleString()}
                            </text>
                          );
                        }}
                        width={45}
                      />
                      <XAxis
                        dataKey="examRound"
                        axisLine={false}
                        tickLine={false}
                        tick={(props) => {
                          const { x, y, payload } = props;
                          return (
                            <g>
                              <text x={x} y={y} dy={5} textAnchor="middle" fontSize={10} fill="#9CA3AF">
                                {payload.value}
                              </text>
                              <text x={x} y={y} dy={16} textAnchor="middle" fontSize={7} fill="#9CA3AF">
                                회차
                              </text>
                            </g>
                          );
                        }}
                        height={35}
                      />
                      <Tooltip
                        cursor={{ stroke: '#9CA3AF', strokeWidth: 1, strokeDasharray: '3 3' }}
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const d = payload[0].payload;
                          const color = getRatingColor(d.rating);
                          const lv = d.rating <= 2000 ? 1 : d.rating <= 4000 ? 2 : d.rating <= 6000 ? 3 : d.rating <= 8000 ? 4 : 5;
                          return (
                            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', minWidth: 120 }}>
                              <div style={{ fontSize: 10, color: '#9CA3AF', marginBottom: 6 }}>
                                {d.examRound}회차 · {d.date}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 16, fontWeight: 700, color }}>
                                  {d.rating.toLocaleString()}
                                </span>
                                {d.change !== null && (
                                  <span style={{ fontSize: 11, fontWeight: 600, color: d.change > 0 ? '#059669' : d.change < 0 ? '#DC2626' : '#9CA3AF' }}>
                                    {d.change > 0 ? '▲' : d.change < 0 ? '▼' : '−'}{d.change > 0 ? '+' : ''}{d.change.toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="rating"
                        stroke="#4ABC85"
                        strokeWidth={2}
                        fill="url(#ratingGradient)"
                        dot={(dotProps) => {
                          const { cx, cy, payload } = dotProps;
                          const color = getRatingColor(payload.rating);
                          return (
                            <circle cx={cx} cy={cy} r={3} fill={color} stroke="#fff" strokeWidth={2} />
                          );
                        }}
                        activeDot={(dotProps) => {
                          const { cx, cy, payload } = dotProps;
                          const color = getRatingColor(payload.rating);
                          return (
                            <circle cx={cx} cy={cy} r={5} fill={color} stroke="#fff" strokeWidth={2} />
                          );
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
            
            {/* 첫 번째 줄: 난이도별 정답률(2칸), 유형별 상세 분석(2칸) */}
            {/* 난이도별 정답률 (2칸) */}
            <div className="page-break-before col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[14px] font-bold text-headerBg">난이도별 정답률</h3>
                  {/* 범례 추가 */}
                  <div className="flex space-x-3">
                    <div className="flex items-center">
                      <div className="mr-1 size-2.5 rounded-3xl bg-primary"></div>
                      <span className="text-[10px] text-gray-500">현재 테스트</span>
                    </div>
                    <div className="flex items-center">
                      <div className="mr-1 size-2.5 rounded-3xl bg-gray-500"></div>
                      <span className="text-[10px] text-gray-500">누적 테스트</span>
                    </div>
                  </div>
                </div>
                {/* 레벨별 데이터 표시 (구조 변경) */}
                <div className="flex gap-2">
                  {/* 왼쪽 회색 박스 (레벨 + 막대 + 퍼센트) */}
                  <div className="flex-1 space-y-4 rounded-3xl bg-gray-50 p-4">
                    {getLevelAchievementData().map(item => (
                      <div key={item.level} className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-[12px]">
                        {/* 레벨 표시 */}
                        <span className="w-10 font-bold" style={{ color: getDifficultyColor(item.level) }}>Lv {item.level}</span>
                        
                        {/* 진행률 바 + 퍼센트 */}
                        <div className="space-y-1.5">
                          {/* 현재 테스트 바 */}
                          <div className="flex items-center">
                            <div className="mr-2 h-[10px] grow overflow-hidden rounded-full bg-gray-100">
                              <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${item.currentAcc}%` }}
                              ></div>
                            </div>
                            <span className="w-8 text-right text-headerBg">{item.currentAcc}%</span>
                          </div>
                          {/* 누적 테스트 바 - 색상 변경 */}
                          <div className="flex items-center">
                            <div className="mr-2 h-[10px] grow overflow-hidden rounded-full bg-gray-100">
                              <div
                                className="h-full rounded-full bg-gray-500"
                                style={{ width: `${item.cumulativeAcc}%` }}
                              ></div>
                            </div>
                            <span className="w-8 text-right text-gray-500">{item.cumulativeAcc}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* 오른쪽 회색 박스 (개수) - 분자/분모 색상 분리 */}
                  <div className="space-y-[18px] rounded-3xl bg-gray-50 p-4"> 
                    {getLevelAchievementData().map(item => (
                      <div key={item.level} className="space-y-1 text-right text-[12px]">
                        {/* 현재 개수: 분자(초록)/분모(검정) */}
                        <div className="block font-semibold">
                          <span className="text-primary">{item.currentCorrect}</span>
                          <span className="text-headerBg">/{item.currentTotal}</span>
                        </div>
                        {/* 누적 개수: 분자(회색)/분모(검정) */}
                        <div className="block font-semibold">
                          <span className="text-gray-500">{item.cumulativeCorrect}</span>
                          <span className="text-headerBg">/{item.cumulativeTotal}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 유형별 상세 분석 테이블 (2칸) */}
            <div className="page-break-before col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              {/* <h3 className="text-[14px] font-bold mb-3 text-headerBg">유형별 상세 분석</h3> */}
              
              {/* 테이블 제목 추가 */}
              <h4 className="mb-3 text-[14px] font-bold text-headerBg">유형별 정답률</h4>
              
              {/* 테이블 영역 - 회색 박스로 감싸기 */}
              <div className="mb-6 pb-2">
                <div className="overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="w-[150px] whitespace-nowrap p-2 text-left font-medium text-gray-600">소단원</th>
                        <th className="w-[100px] whitespace-nowrap p-2 text-left font-medium text-gray-600">정답률</th>
                        <th className="w-[100px] whitespace-nowrap p-2 text-left font-medium text-gray-600">누적 정답률</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getDetailedAnalysisData().map((item, index) => {
                        // 정답률 문자열 파싱 ('XX% (Y/Z)' 형식)
                        const accuracyMatch = item.accuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/);
                        const cumulativeMatch = item.cumulativeAccuracy ? item.cumulativeAccuracy.match(/(\d+)%\s*\((\d+)\/(\d+)\)/) : null;
                        
                        return (
                          <tr key={index} className={`${index === getDetailedAnalysisData().length - 1 ? '' : 'border-b'} border-gray-200`}>
                            <td className="truncate px-2 py-3 align-middle text-headerBg" style={{ maxWidth: '150px' }}>
                              {item.subCategory}
                            </td>
                            <td className="whitespace-nowrap px-2 py-3 align-middle font-bold">
                              {accuracyMatch ? (
                                <>
                                  <span className="text-primary">{accuracyMatch[2]}</span>
                                  <span className="text-headerBg">/{accuracyMatch[3]}</span>
                                  <span className="ml-1 text-[10px] text-gray-600">({accuracyMatch[1]}%)</span>
                                </>
                              ) : (
                                item.accuracy
                              )}
                            </td>
                            <td className="whitespace-nowrap px-2 py-3 align-middle font-bold">
                              {cumulativeMatch ? (
                                <>
                                  <span className="text-gray-500">{cumulativeMatch[2]}</span>
                                  <span className="text-headerBg">/{cumulativeMatch[3]}</span>
                                  <span className="ml-1 text-[10px] text-gray-600">({cumulativeMatch[1]}%)</span>
                                </>
                              ) : (
                                item.cumulativeAccuracy || '0% (0/0)'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* 두 번째 줄: 난이도별 성취도(2칸), 유형별 성취도(2칸) */}
            {/* 난이도별 성취도 (2칸) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-6">
                <h3 className="mb-3 text-[14px] font-bold text-headerBg">난이도별 성취도</h3>
                <div className="mb-2 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexLevel}
                        activeShape={renderActiveShape}
                        data={getLevelDonutData()}
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="80%"
                        paddingAngle={5}
                        cornerRadius={8}
                        dataKey="value"
                        onMouseEnter={onLevelPieEnter}
                      >
                        {getLevelDonutData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={LEVEL_PASTEL_COLORS[index % LEVEL_PASTEL_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* 범례 */}
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                  {levelLegend.map((legend, index) => (
                    <div key={legend.name} className="flex items-center">
                      <div className="mr-1 size-2.5 rounded-3xl" style={{ backgroundColor: LEVEL_PASTEL_COLORS[index % LEVEL_PASTEL_COLORS.length] }}></div>
                      <span className="text-[10px] font-bold" style={{ color: LEVEL_SOLID_COLORS[index % LEVEL_SOLID_COLORS.length] }}>{legend.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 유형별 성취도 (2칸) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-6">
                <h3 className="mb-3 text-[14px] font-bold text-headerBg">유형별 성취도</h3>
                <div className="mb-2 h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        activeIndex={activeIndexDomain}
                        activeShape={renderActiveShape}
                        data={getDomainDonutData()}
                        cx="50%"
                        cy="50%"
                        innerRadius="60%"
                        outerRadius="80%"
                        cornerRadius={8}
                        paddingAngle={5}
                        dataKey="value"
                        onMouseEnter={onDomainPieEnter}
                      >
                        {getDomainDonutData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={DOMAIN_GREEN_COLORS[index % DOMAIN_GREEN_COLORS.length]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {/* 범례 */}
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                  {getDomainLegend().map((legend, index) => (
                    <div key={legend.name} className="flex items-center">
                      <div className="mr-1 size-2.5 rounded-3xl" style={{ backgroundColor: legend.color }}></div>
                      <span className="text-[10px] text-gray-600">{legend.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 세 번째 줄: 난이도별 정답률 방사형 바 차트(2칸), 주요 유형별 정답률(2칸) */}
            {/* 난이도별 정답률 방사형 바 차트 (2칸) */}
            <div className="page-break-before col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[14px] font-bold text-headerBg">난이도별 정답률</h3>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      cx="50%"
                      cy="50%"
                      innerRadius="20%"
                      outerRadius="90%"
                      barSize={12}
                      data={[...levelRadarData()].reverse()}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <RadialBar
                        background={{ fill: '#F3F4F6' }}
                        dataKey="A"
                        cornerRadius={6}
                      >
                        {[...levelRadarData()].reverse().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={LEVEL_PASTEL_COLORS[entry.level - 1]} />
                        ))}
                      </RadialBar>
                      <Tooltip
                        content={({ active, payload }) => {
                          if (!active || !payload || !payload.length) return null;
                          const d = payload[0].payload;
                          const color = LEVEL_SOLID_COLORS[d.level - 1];
                          return (
                            <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 10px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color }}>{d.subject}</span>
                              <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 6 }}>{d.A}%</span>
                            </div>
                          );
                        }}
                      />
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                {/* 범례 */}
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
                  {levelLegend.map((legend, index) => (
                    <div key={legend.name} className="flex items-center">
                      <div className="mr-1 size-2.5 rounded-3xl" style={{ backgroundColor: LEVEL_PASTEL_COLORS[index] }}></div>
                      <span className="text-[10px] font-bold" style={{ color: LEVEL_SOLID_COLORS[index] }}>{legend.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 주요 유형별 정답률 (2칸) */}
            <div className="page-break-before col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-6">
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[14px] font-bold text-headerBg">주요 유형별 정답률</h3>
                  <div className="flex items-center">
                    <div className="mr-1 size-3 rounded-3xl bg-primary"></div>
                    <span className="text-[10px] text-gray-600">정답률 (%)</span>
                  </div>
                </div>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart cx="50%" cy="50%" outerRadius="70%" data={getSubjectRadarData()}>
                      <PolarGrid stroke="#E0E0E0" />
                      <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                      <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                      <Tooltip
                        contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                        formatter={(value, name, props) => [`${props.payload.subject}: ${value}%`, null]}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            

            {/* --- START: Reordered Sections --- */}

            {/* 강점 섹션 (2칸) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ThumbsUp size={14} className="text-emerald-700" />
                  <h3 className="text-[14px] font-bold text-headerBg">강점</h3>
                </div>

                {/* 수정 버튼 추가 */}
                {!isEditingStrengths && reportData?.data?.status !== 999 && (
                  <button 
                    onClick={handleEditStrengths}
                    className="rounded border border-gray-300 px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                  >
                    수정
                  </button>
                )}
              </div>
              
              {/* 수정 모드 UI */}
              {isEditingStrengths ? (
                <div className="space-y-3">
                  {tempStrengths.map((item, index) => (
                    <div key={index} className="rounded-3xl bg-gray-50 p-3">
                      <textarea
                        value={item.description}
                        onChange={(e) => handleTempStrengthChange(index, e.target.value)}
                        className="min-h-[60px] w-full resize-none rounded border border-gray-300 p-1 text-[12px]"
                      />
                    </div>
                  ))}
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={handleCancelStrengths}
                      className="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveStrengths}
                      className="rounded bg-primary px-2 py-1 text-xs text-white hover:bg-[#3a9e70]"
                    >
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {getStrengthsAndWeaknesses().strengths.map((item, index) => (
                    <div key={index} className="rounded-r-3xl border-l-4 border-emerald-400 bg-gray-50 p-3">
                      {renderStructuredText(item.description, 'strength')}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 약점 섹션 (2칸) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-2">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ThumbsDown size={14} className="text-red-600" />
                  <h3 className="text-[14px] font-bold text-headerBg">약점</h3>
                </div>

                {/* 수정 버튼 추가 */}
                {!isEditingWeaknesses && reportData?.data?.status !== 999 && (
                  <button 
                    onClick={handleEditWeaknesses}
                    className="rounded border border-gray-300 px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                  >
                    수정
                  </button>
                )}
              </div>
              
              {/* 수정 모드 UI */}
              {isEditingWeaknesses ? (
                <div className="space-y-3">
                  {tempWeaknesses.map((item, index) => (
                    <div key={index} className="rounded-3xl bg-gray-50 p-3">
                      <textarea
                        value={item.description}
                        onChange={(e) => handleTempWeaknessChange(index, e.target.value)}
                        className="min-h-[60px] w-full resize-none rounded border border-gray-300 p-1 text-[12px]"
                      />
                    </div>
                  ))}
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={handleCancelWeaknesses}
                      className="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveWeaknesses}
                      className="rounded bg-primary px-2 py-1 text-xs text-white hover:bg-[#3a9e70]"
                    >
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {getStrengthsAndWeaknesses().weaknesses.map((item, index) => (
                    <div key={index} className="rounded-r-3xl border-l-4 border-red-400 bg-gray-50 p-3">
                      {renderStructuredText(item.description, 'weakness')}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 공부가 필요한 영역 (2칸) */}
            <div className="col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-4">
              <h3 className="mb-4 text-[14px] font-bold text-headerBg">공부가 필요한 영역</h3>
              <div className="space-y-4">
                {getStudyAreas().map((area, index) => (
                  <div key={index} className="rounded-3xl bg-gray-50 p-4">
                    <div className="mb-3 flex items-center">
                      {/* CHECK 배지 */}
                      <span className="mr-2 inline-flex h-[16px] items-center justify-center rounded-full bg-primary px-2 text-[8px] font-bold text-white">
                        CHECK
                      </span>
                      <span className="text-[14px] font-semibold text-headerBg">{area.title}</span>
                    </div>
                    <ul className="list-inside list-disc space-y-1.5 text-[12px] text-gray-700">
                      {area.topics.map((topic, topicIndex) => (
                        <li key={topicIndex}>{topic}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* 개선 방향 및 학습 조언 (4칸) */}
            <div className="page-break-before col-span-1 rounded-3xl bg-white p-4 shadow-[0_0_6px_rgba(0,0,0,0.1)] sm:col-span-2 md:col-span-4">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-[14px] font-bold text-headerBg">개선 방향 및 학습 조언</h3>
                
                {/* 수정 버튼 추가 */}
                {!isEditingLearningGoals && reportData?.data?.status !== 999 && (
                  <button 
                    onClick={handleEditLearningGoals}
                    className="rounded border border-gray-300 px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                  >
                    수정
                  </button>
                )}
              </div>
              
              {/* 각 목표 */}
              {isEditingLearningGoals ? (
                <div className="mb-6">
                  {tempLearningGoals && tempLearningGoals.map((goal, index) => (
                    <div key={index} className="mb-6">
                      <div className="mb-3 flex items-center">
                        <h3 className="text-[14px] font-bold text-headerBg">{goal.title}</h3>
                      </div>
                      <div className="rounded-3xl bg-gray-50 p-4">
                        <textarea
                          value={goal.description}
                          onChange={(e) => handleTempLearningGoalChange(index, e.target.value)}
                          className="min-h-[80px] w-full resize-none rounded border border-gray-300 p-2 text-[12px]"
                        />
                      </div>
                    </div>
                  ))}
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      onClick={handleCancelLearningGoals}
                      className="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleSaveLearningGoals}
                      className="rounded bg-primary px-2 py-1 text-xs text-white hover:bg-[#3a9e70]"
                    >
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {getLearningGoals().goals.map((goal, index) => (
                    <div key={index} className="mb-6">
                      <div className="mb-3 flex items-center">
                        <h3 className="text-[14px] font-bold text-headerBg">{goal.title}</h3>
                      </div>
                      <div className="rounded-3xl bg-gray-50 p-4">
                        <p className="text-[12px] leading-relaxed text-headerBg">{renderHighlightedText(goal.description)}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* 종합 평가 및 코멘트 */}
              <div>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-[14px] font-bold text-headerBg">종합 평가 및 코멘트</h3>
                  
                  {/* 수정 버튼 추가 */}
                  {!isEditingOverallComment && reportData?.data?.status !== 999 && (
                    <button 
                      onClick={handleEditOverallComment}
                      className="rounded border border-gray-300 px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                    >
                      수정
                    </button>
                  )}
                </div>
                
                {isEditingOverallComment ? (
                  <div className="rounded-3xl bg-gray-50 p-4">
                    <textarea
                      value={tempOverallComment}
                      onChange={(e) => setTempOverallComment(e.target.value)}
                      className="min-h-[120px] w-full resize-none rounded border border-gray-300 p-2 text-[12px]"
                    />
                    <div className="mt-3 flex justify-end gap-2">
                      <button
                        onClick={handleCancelOverallComment}
                        className="rounded bg-gray-200 px-2 py-1 text-xs hover:bg-gray-300"
                      >
                        취소
                      </button>
                      <button
                        onClick={handleSaveOverallComment}
                        className="rounded bg-primary px-2 py-1 text-xs text-white hover:bg-[#3a9e70]"
                      >
                        저장
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-3xl bg-gray-50 p-4">
                    <p className="text-[12px] leading-relaxed text-headerBg">
                      {renderHighlightedText(getLearningGoals().overallComment)}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* --- END: Reordered Sections --- */}
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ReportModal; 